"use client";
import { useState } from "react";
import Image from "next/image";
import { IndianRupee, MapPin, Loader2 } from "lucide-react";
import CreateLocaitonModal from "@/components/custom/createLocation/CreateLocaitonModal";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import useAuth from "@/hooks/useAuth";
// 👇 1. Import standard axios (not the private hook)
import axios from "axios"; 

// Define Backend URL
const BACKEND_URL = process.env.NEXT_PUBLIC_API_URL || "https://invest.chargemod.com/api";

export default function InvestPage() {
  const [hovered, setHovered] = useState(null);
  const [open, setOpen] = useState(false);

  // 2. Get auth AND setAuth from context
  const { auth, setAuth } = useAuth();

  // 3. Check Auth Logic (Using Standard Axios to avoid Forced Redirects)
  const { data: authData, isLoading } = useQuery({
    queryKey: ["auth-verification-landing"],
    queryFn: async () => {
      // A. If we already have an access token in memory, we are logged in.
      if (auth?.accessToken) {
        return { success: true };
      }

      // B. If no token in memory, try to refresh silently using the Cookie.
      try {
        // We use standard 'axios' here, NOT 'axiosPrivate'.
        // This prevents the global interceptor from forcing a redirect if this fails.
        const res = await axios.get(
          `${BACKEND_URL}/distributor-user/refresh-token`,
          { withCredentials: true } // Critical: Sends the HttpOnly Cookie
        );

        // If successful, update the global Auth Context
        if (res.data?.accessToken) {
          setAuth((prev) => ({
            ...prev,
            accessToken: res.data.accessToken,
            id: res.data.distributorId || prev.id, // Adjust based on your API response
          }));
          return { success: true };
        }
      } catch (error) {
        // C. If this fails, the user is simply a GUEST.
        // We return false. We do NOT redirect.
        return { success: false };
      }
      
      return { success: false };
    },
    retry: false,
  });

  const isLoggedIn = authData?.success;

  return (
    <div className="relative bg-white text-black min-h-screen flex items-center px-6 md:px-16 overflow-hidden">
      {/* FLOOR ANGLE */}
      <div className="absolute lg:bottom-0 lg:left-0 md:bottom-0 md:left-0 sm:bottom-[180px] sm:left-[0px] bottom-[40px] left-[0px] w-full h-[200px] bg-[#2E2E2E] skew-y-2 origin-bottom-left z-0" />
      {/* Patch for bottom-left white triangle */}
      <div className="absolute lg:bottom-0 lg:left-0 md:bottom-0 md:left-0 bottom-[180px] left-[-20px] lg:w-[100px] md:w-[100px] w-100 lg:h-[100px] md:h-[200px] h-full bg-[#2E2E2E] lg:rotate-[45deg] rotate-90 origin-bottom-left z-0" />

      <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between relative z-10">
        {/* LEFT TEXT SECTION */}
        <div className="text-left space-y-6 max-w-xl z-20 lg:mb-0 md:mb-[300px] mb-[-300px]">
          <p className="text-green-600 font-semibold lg:text-3xl text-xl lg:pt-0 md:pt-5 pt-30">
            JOIN THE EV REVOLUTION
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold leading-snug">
            Invest in the <br />
            <span className="font-black">Future of Mobility.</span>
          </h1>
          {/* BUTTONS */}
          <div className="flex flex-row gap-4 pt-4">
            {/* INVEST BUTTON */}
            <Link href="/invest">
              <button
                onMouseEnter={() => setHovered("invest")}
                onMouseLeave={() => setHovered(null)}
                className="flex flex-col items-start group cursor-pointer"
              >
                <div className="bg-black text-white px-2 text-[12px] font-bold py-1 rounded-t-md ml-2 group-hover:bg-green-500 transition-colors duration-300 ease-in-out">
                  I WANT TO
                </div>
                <div className="bg-green-500 group-hover:bg-black text-white px-4 py-2 rounded-md flex items-center gap-2 lg:text-2xl text-[12px] font-semibold shadow-md transition-colors duration-300 ease-in-out">
                  <IndianRupee className="w-5 h-5" />
                  <span className="transition-colors duration-300 ease-in-out">
                    Invest
                  </span>
                </div>
              </button>
            </Link>

            {/* LOCATION BUTTON LOGIC */}
            {isLoading ? (
               <div className="flex items-center justify-center px-4">
                 <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
               </div>
            ) : isLoggedIn ? (
              <>
                <button
                  onMouseEnter={() => setHovered("location")}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setOpen(true)}
                  className="flex flex-col items-start group cursor-pointer"
                >
                  <div className="bg-black text-white px-2 text-[12px] font-bold py-1 rounded-t-md ml-2 group-hover:bg-green-500 transition-colors duration-300 ease-in-out">
                    I HAVE A
                  </div>
                  <div className="bg-green-500 group-hover:bg-black text-white px-4 py-2 rounded-md flex items-center gap-2 lg:text-2xl text-[12px] font-semibold shadow-md transition-colors duration-300 ease-in-out">
                    <MapPin className="w-5 h-5" />
                    <span className="transition-colors duration-300 ease-in-out">
                      Location
                    </span>
                  </div>
                </button>

                <CreateLocaitonModal open={open} onOpenChange={setOpen} />
              </>
            ) : null}
          </div>
        </div>
        {/* RIGHT IMAGE SECTION */}
        <div className="relative w-full md:w-[700px] h-[600px] mt-10 md:mt-0 lg:mb-0 md:mb-[0px] mb-[50px] lg:flex-1">
          <Image
            src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/PetrolPump.svg"
            alt="Charger Station"
            width={1000}
            height={600}
            className="hidden min-[427px]:block absolute lg:bottom-[10px] lg:right-0 md:bottom-[10px] bottom-[120px] right-[-10px] z-10"
          />
          {/* CAR */}
          {(hovered === "invest" || hovered === "location") && (
            <Image
              src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/whiteCar.svg"
              alt="Car"
              width={400}
              height={300}
              className="absolute lg:bottom-[10px] lg:left-[-240px] md:bottom-[0px] md:left-[-240px] bottom-[80px] left-[-100px] z-20 transition-opacity duration-300"
            />
          )}
          {/* MAN */}
          <Image
            src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/boy.svg"
            alt="Man"
            width={180}
            height={300}
            className={`absolute lg:bottom-[-20px] lg:left-[230px] md:bottom-[-40px] md:left-[230px] bottom-[40px] left-[220px] z-30 transition-opacity duration-300 ${
              hovered === "invest" ? "opacity-100" : "opacity-0"
            }`}
          />
          {/* WOMAN */}
          <Image
            src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/girl.svg"
            alt="Woman"
            width={150}
            height={250}
            className={`absolute lg:bottom-[-30px] lg:left-[400px] md:bottom-[-40px] md:left-[240px] bottom-[40px] left-[100px] z-30 transition-opacity duration-300 ${
              hovered === "location" ? "opacity-100" : "opacity-0"
            }`}
          />
          {/* MOBILE VIEW IMAGES */}
          <div className="hidden max-[426px]:block">
            <Image
              src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/PetrolPump.svg"
              alt="Charger Station"
              width={700}
              height={250}
              className="max-[426px]:block absolute lg:bottom-[-30px] lg:right-0 md:bottom-[10px] bottom-[120px] right-[-10px] z-10"
            />
            <Image
              src="https://cmod-console-prod.s3.ap-south-1.amazonaws.com/invest-chargemod/whiteCar.svg"
              alt="Car"
              width={170}
              height={100}
              className="absolute bottom-[70px] right-[0px] z-20 transition-opacity duration-300"
            />
            <Image
              src="/investPerson.png"
              alt="Man"
              width={100}
              height={180}
              className="absolute bottom-[70px] left-[-20px] z-30 transition-opacity duration-300"
            />
            <Image
              src="/investWomen.png"
              alt="Woman"
              width={80}
              height={180}
              className="absolute bottom-[70px] left-[40px] z-30 transition-opacity duration-300"
            />
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 w-full h-10 bg-green-600 overflow-hidden z-50 flex items-center">
        <div className="marquee-wrapper">
          <div className="marquee-track text-white font-semibold text-sm sm:text-base">
            {Array(10)
              .fill(null)
              .map((_, i) => (
                <span key={i} className="flex items-center gap-6">
                  <span>INVEST IN EV CHARGING STATION</span>
                  <span className="text-white font-bold">✦</span>
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
